<template>
  <div class="import-container">
    <Progress v-if="importing" :progress="progress" />
    <div v-if="showProgressStalledContent" class="import-progress-stalled-container">
      <p class="import-progress-stalled-text">
        {{ progressStalledText }}
      </p>
      <FriedButton
        data-external="import-progress-stalled-button"
        @click="handleClickProgressStalled"
      >
        {{ progressStalledButtonText }}
      </FriedButton>
    </div>
    <template v-if="showWordMergeModal">
      <Prompt
        v-if="canSaveAsDynamicTemplate"
        :title="t('save-dynamic-template-header')"
        :text="t('save-dynamic-template-description')"
        :main-button-text="t('save')"
        @confirm="handleSaveDynamicWordMergeTemplate"
        @close="handleCancelDynamicWordMergeTemplate"
        @cancel="handleCancelDynamicWordMergeTemplate"
      />
      <WordMergeFieldsModal
        v-else
        :word-merge="wordMerge"
        @save="handleProcessWordDocument($event, payload.documentId)"
        @cancel="$emit('cancel')"
      />
    </template>
  </div>
</template>
<script lang="ts">
import type { Channel } from '@getaccept/pusher';
import { DocumentEvent } from '@getaccept/pusher';
import Prompt from '@getaccept/lib-shared/src/components/Prompt.vue';
import { FileHelper } from '@getaccept/lib-shared/src/file/helpers/file.helper';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { ExternalEditorType } from '@getaccept/lib-shared/src/enums/ExternalEditorType';
import type { PropType } from 'vue';
import { computed, ref, onMounted, onBeforeUnmount, watch, defineComponent } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { pusherClientInternal } from '@getaccept/lib-shared/src/internal/pusher';
import { ImportType } from '../types/enums/import-type';
import type { ImportProgress } from '../types/import-progress';
import WordMergeFieldsModal from '../word-merge/components/WordMergeFieldsModal.vue';
import type { ImportFiles } from '../types/import-files';
import type { ImportPayload } from '../types/import-payload';
import { ImportStatus } from '../types/enums/import-status';
import type { DocumentImportData } from '../../../../document-edit/assembler/types/document-import-data';
import type { WordMergeField } from '../../../../api/word/types/word-merge-field';
import { useImporter } from '../composables/importer.composable';
import Progress from './Progress.vue';

export default defineComponent({
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Progress,
    Prompt,
    WordMergeFieldsModal,
  },
  props: {
    payload: { type: Object as PropType<ImportPayload>, required: true },
    progressStalledText: { type: String, default: '' },
    progressStalledButtonText: { type: String, default: '' },
    documentPusherChannel: { type: Object as PropType<Channel>, required: true },
  },
  emits: [
    'importing',
    'templateImported',
    'pagesImported',
    'progressStalled',
    'progressStalledClicked',
    'cancel',
    '',
    'error',
  ],
  setup(props, { emit }) {
    const {
      importTemplate,
      startLoadingPages,
      loadPage,
      importFiles,
      importWordDocument,
      processWordDocument,
      importGoogleDrive,
      saveWordMergeDynamicTemplate,
      setWordMergeProperties,
      reset,
      setImportFailed,
      importing,
      importData: data,
      imported,
      wordMerge,
      importedDocument,
      importedPages,
      progress,
      showWordMergeModal,
    } = useImporter();

    let progressStalledTimer = null;
    const showProgressStalledContent = ref(false);

    watch(importing, () => {
      if (importing.value) {
        emit('importing', title.value);
      }
    });

    watch(imported, () => {
      let event: 'pagesImported' | 'templateImported' = null;
      let payload: any = null;
      switch (data.value.type) {
        case ImportType.GoogleDocs:
        case ImportType.Template: {
          event = 'templateImported';
          payload = importedDocument.value;
          break;
        }
        case ImportType.WordDocument:
        case ImportType.GoogleDrive:
        case ImportType.File: {
          event = 'pagesImported';
          payload = importedPages.value;
          break;
        }
      }
      if (!event) {
        return;
      }
      setTimeout(() => {
        emit(event, payload);
      }, 1500);
    });

    watch(
      progress,
      (newValue: ImportProgress, previousValue: ImportProgress) => {
        const { status: prevStatus, percentage: prevPercentage } = previousValue || {};
        const { status: currStatus, percentage: currPercentage } = newValue || {};

        if (currStatus === prevStatus && currPercentage === prevPercentage) {
          return;
        }
        if (newValue.status === ImportStatus.Error) {
          emit('error');
        }

        showProgressStalledContent.value = false;
        clearTimeout(progressStalledTimer);
        progressStalledTimer = setTimeout(() => {
          handleProgressStalled();
        }, 30000);
      },
      { immediate: true }
    );

    const title = computed(() => data.value?.title);

    const canSaveAsDynamicTemplate = computed(
      () => props.payload.documentType === EnumDocumentType.Template && wordMerge.value?.isDynamic
    );

    const handleProcessWordDocument = (
      {
        missing,
        name,
        pageIndex,
      }: {
        missing: WordMergeField[];
        name: string;
        pageIndex: number;
      },
      documentId: string
    ) => {
      processWordDocument({ documentId, pageIndex, missing, name });
    };

    const handleSaveDynamicWordMergeTemplate = () => {
      saveWordMergeDynamicTemplate({
        documentId: props.payload.documentId,
        name: wordMerge.value?.filename,
        pageIndex: props.payload.pageIndex,
      });
    };

    const handleCancelDynamicWordMergeTemplate = () => {
      setWordMergeProperties({ isDynamic: false });
    };

    const handleProgressStalled = () => {
      emit('progressStalled');
      showProgressStalledContent.value = true;
    };

    const handleClickProgressStalled = () => {
      emit('progressStalledClicked');
    };

    const addSubscriptions = () => {
      if (props.documentPusherChannel) {
        pusherClientInternal.listen(
          props.documentPusherChannel,
          DocumentEvent.DocumentPageImageDone,
          handlePageImageDone
        );
        pusherClientInternal.listen(
          props.documentPusherChannel,
          DocumentEvent.DocumentPageImagesStarted,
          handlePageImageStarted
        );
        pusherClientInternal.listen(
          props.documentPusherChannel,
          DocumentEvent.DocumentImportFailed,
          handleDocumentImportFailed
        );
      }
    };

    const handlePageImageStarted = ({
      documentPageImagesStarted,
    }: {
      documentPageImagesStarted: DocumentImportData;
      entityId: string;
      documentId: string;
    }) => {
      startLoadingPages(documentPageImagesStarted);
    };

    const handleDocumentImportFailed = ({
      documentImportFailed,
    }: {
      documentImportFailed: { message: string };
    }) => {
      setImportFailed(new Error(documentImportFailed.message));
    };

    const handlePageImageDone = (payload: { pageId: string }) => {
      const isGoogleDocTemplate =
        props.payload.template?.externalEditorType === ExternalEditorType.Docs;

      loadPage({
        documentId: props.payload.documentId,
        id: payload.pageId,
        isGoogleDocTemplate,
      });
    };

    const stopSubscriptions = () => {
      if (props.documentPusherChannel) {
        pusherClientInternal.unbind(
          props.documentPusherChannel,
          DocumentEvent.DocumentPageImageDone,
          handlePageImageDone
        );
        pusherClientInternal.unbind(
          props.documentPusherChannel,
          DocumentEvent.DocumentPageImagesStarted,
          handlePageImageStarted
        );
        pusherClientInternal.unbind(
          props.documentPusherChannel,
          DocumentEvent.DocumentImportFailed,
          handleDocumentImportFailed
        );
      }
    };

    onMounted(() => {
      addSubscriptions();
      if (props.payload.template) {
        const { template, pageIndex, documentId } = props.payload;

        importTemplate({
          templateId: template.id,
          name: template.name,
          externalEditorType: template.externalEditorType,
          pageIndex,
          documentId,
        });
      } else if (props.payload.files) {
        const { files, pageIndex, documentId } = props.payload;
        const extension: string = FileHelper.extractFileType(files[0].name);
        const payload: ImportFiles = {
          pageIndex,
          documentId,
          files,
        };

        if (['.docx'].includes(extension)) {
          payload.files = [files[0]];
          importWordDocument(payload);
          return;
        }

        importFiles(payload);
      } else if (props.payload.fileUrl) {
        const { fileName, fileUrl, pageIndex, documentId } = props.payload;

        importGoogleDrive({
          fileName,
          fileUrl,
          pageIndex,
          documentId,
        });
      }
    });

    onBeforeUnmount(() => {
      stopSubscriptions();
      reset();
      clearTimeout(progressStalledTimer);
    });

    return {
      importing,
      progress,
      showProgressStalledContent,
      showWordMergeModal,
      canSaveAsDynamicTemplate,
      wordMerge,
      handleClickProgressStalled,
      handleSaveDynamicWordMergeTemplate,
      handleCancelDynamicWordMergeTemplate,
      handleProcessWordDocument,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/lib-shared/src/alert-variables';

.import-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .import-progress-stalled-container {
    display: flex;
    justify-content: space-between;
    background: var(--info-light);
    border-radius: var(--radius-medium);
    margin-top: var(--spacing-100);
    padding: var(--spacing-100);

    .import-progress-stalled-text {
      padding-right: var(--spacing-100);
      flex-basis: 66%;
    }
  }
}
</style>
